<template>
  <template v-if="init || layout === 'check-layout'">
    <component :is="layout" :padding="padding">
      <router-view />
    </component>
    <Modal />
  </template>
  <CustomLoader v-else />
</template>

<script setup>
import { computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import CustomLoader from '@/components/common/CustomLoader'
import CheckLayout from '@/components/layout/CheckLayout.vue'
import EmptyLayout from '@/components/layout/EmptyLayout.vue'
import MainLayout from '@/components/layout/MainLayout.vue'
import Modal from '@/components/modal/index.vue'

import { PAGES } from '@/constants'
import { notInitRoutes } from '@/router'
import { storeToRefs } from 'pinia'
import { useAppStore } from './services/store/app'
import cookiesService from './services/cookiesService'

const appStore = useAppStore()
const { init, auth } = storeToRefs(appStore)
const route = useRoute()
const router = useRouter()

const layouts = { empty: EmptyLayout, check: CheckLayout, main: MainLayout }
const layout = computed(() => layouts[route.meta.layout || 'empty'])

const padding = computed(() =>
  route.meta.padding === undefined ? true : route.meta.padding
)

function parseQuery(str) {
  if (!str) return {}
  if (str.charAt(0) === '?' || str.charAt(0) === '#') str = str.slice(1)
  const params = {}
  str.split('&').forEach((part) => {
    const eq = part.indexOf('=')
    if (eq === -1) return
    const key = decodeURIComponent((part.slice(0, eq) || '').replace(/\+/g, ' '))
    const value = decodeURIComponent((part.slice(eq + 1) || '').replace(/\+/g, ' '))
    if (key) params[key] = value
  })
  return params
}

function safeDestPath(dest) {
  if (!dest || typeof dest !== 'string') return ''
  if (!dest.startsWith('/') || dest.startsWith('//')) return ''
  if (dest.startsWith(PAGES.login) || dest.startsWith(PAGES.loginKeycloakCallback)) return ''
  return dest
}

function gotoLogin() {
  const current = safeDestPath(
    (window.location.pathname || '') + (window.location.search || '') + (window.location.hash || '')
  )
  router.push(current ? { path: PAGES.login, query: { redirect: current } } : PAGES.login)
}

onMounted(() => {
  const pathname = window.location.pathname || ''
  const search = window.location.search || ''
  if (pathname.includes('login/keycloak/callback')) {
    const params = { ...parseQuery(window.location.hash || ''), ...parseQuery(search) }
    const access = params.access
    const refresh = params.refresh
    if (access && refresh) {
      cookiesService.getService().setToken({
        access,
        refresh,
        auth_type: params.auth_type || 'keycloak',
      })
      appStore.setAuth()
      appStore.setInit()
      appStore.getUserData()
      appStore.getCitiesData()
      const dest = safeDestPath(params.dest)
      router.replace(dest || PAGES.main)
      return
    }
    if (params.error) {
      appStore.setInit()
      appStore.setKeycloakError(params.error)
      router.replace({ path: PAGES.login })
      return
    }
  }
  const isNotInitRoute = !!notInitRoutes?.find((r) =>
    window.location.pathname?.includes(r)
  )
  if (isNotInitRoute) return
  appStore.initialize()
})

watch(init, (newVal) => {
  if (newVal) {
    if (auth.value) {
      appStore.getUserData()
      appStore.getCitiesData()
    } else {
      gotoLogin()
    }
  }
})

watch(auth, (newVal) => {
  if (init.value && !newVal) {
    gotoLogin()
  }
})
</script>
